import React from 'react';
import { Def, DefTable } from '~/components/DefTable';
import WaitFor from '~/components/WaitFor';
import { Balance } from '~/lib/ApiModel';
import { getAppContext } from '~/lib/AppContext';
import { nearestCent } from '~/lib/helper/CurrencyHelper';
import { formatCurrency } from '~/lib/helper/StringHelper';
import { AsyncStore } from '~/lib/store/AsyncStore';

function calculateFinalPrice(subtotal: number, customerCredit: number) {
    // Get tax code from app context
    const taxCode = getAppContext().customer.item?.tax_code ?? { type: 'none', name: 'Tax' };

    // Calculate tax if applicable
    let tax = 0;
    if (taxCode.type === 'scalar' && taxCode.fixed_percent) {
        tax = nearestCent((subtotal * taxCode.fixed_percent) / 100);
    }

    // Calculate total cost
    const totalCost = nearestCent(subtotal + tax);

    // Calculate amount due after credit
    const due = Math.max(0, totalCost - customerCredit);

    return {
        subtotalCost: subtotal,
        taxName: taxCode.type === 'none' ? null : taxCode.name,
        tax: tax === 0 ? null : formatCurrency(tax),
        totalCost: totalCost,
        totalDue: due,
    };
}

type DomainBillingDetailsProps = {
    subtotal: number;
};

export default class DomainBillingDetails extends React.Component<DomainBillingDetailsProps> {
    balanceStore = new AsyncStore<Balance, undefined>(() => getAppContext().apiClient.getBalance(), undefined);

    componentDidMount(): void {
        void this.balanceStore.load();
    }

    render() {
        return (
            <div className="domainBillingDetails">
                <WaitFor operation={this.balanceStore}>
                    {() => {
                        const creditBalance = this.balanceStore.item?.available_credit ?? 0;

                        const cost = calculateFinalPrice(this.props.subtotal, creditBalance);

                        const hasCreditCard = (
                            getAppContext().customer.item?.configured_payment_methods ?? []
                        ).includes('credit-card');
                        return (
                            <DefTable className="wide">
                                <Def name="Total Cost">
                                    {formatCurrency(cost.totalCost)}{' '}
                                    {cost.tax && `(incl. ${cost.tax} ${cost.taxName ?? ''})`}
                                </Def>
                                {creditBalance > 0 && <Def name="In-store Credit">{formatCurrency(creditBalance)}</Def>}
                                <Def name="Total Due">{formatCurrency(cost.totalDue)}</Def>
                                <Def name="Payment Method" className="mt-2">
                                    {hasCreditCard ? 'Credit Card' : 'PayPal'}
                                </Def>
                            </DefTable>
                        );
                    }}
                </WaitFor>
            </div>
        );
    }
}
